import moment from "moment";
import React, { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { getInitialsFromFullName } from "../../../constants/constants";
import { useAppSelector } from "../../../redux/redux";
import {
  IEditInvoice,
  useGetInvoiceByIdQuery,
  useGetInvoiceTermsQuery,
  useGetPaymentsRecievedQuery,
  useGetServicesQuery,
  useGetTaxRateQuery,
  useUpdateBilledStatusMutation,
  useUpdateInvoiceMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useAddBilledWorkMutation,
  useGetUsersQuery,
} from "../../../slices/UserSlice";
import { SelectOptionType } from "../../../types/types";
import {
  InvoiceFormInitialValues,
  InvoiceFormSubmitValues,
} from "./form/invoice-form.types";
import {
  billedWorkRecords,
  createDefaultInvoiceItem,
  parseMoney,
} from "./form/invoice-form.utils";
import { InvoiceForm } from "./form/InvoiceForm";

const discountTypes = {
  "%": "89EDC863-9055-479B-AD08-554C0418A59F",
  $: "771B703A-5E94-4271-988A-206C0B48F7A8",
};

const findMissingElements = (array1: string[], array2: string[]) =>
  array1?.filter((item) => !array2?.includes(item));

const EditInvoice = ({
  setView,
}: {
  setView: React.Dispatch<React.SetStateAction<"details" | "edit">>;
}) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.appReducer.user);
  const { id } = useParams<{ id: string }>();
  const [, setSearchParams] = useSearchParams();

  const orgId = getLinkedFirm()?.orgId || "";

  const [updateInvoice, { isLoading: isLoadingUpdate }] =
    useUpdateInvoiceMutation();
  const [updateBilledStatus] = useUpdateBilledStatusMutation();
  const [addBilledWork] = useAddBilledWorkMutation();

  const { data: invoice, isLoading: isLoadingInvoice } = useGetInvoiceByIdQuery(
    `${id}`,
  );

  const { data: paymentsRecieved, isLoading: isLoadingPaymentsRecieved } =
    useGetPaymentsRecievedQuery(`${id}`);

  const { data: terms, isLoading: isLoadingTerms } = useGetInvoiceTermsQuery({
    orgId,
  });

  const { data: services, isLoading: isLoadingServices } =
    useGetServicesQuery(orgId);

  const { data: taxRateData, isLoading: isLoadingTaxRate } =
    useGetTaxRateQuery(orgId);

  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery(orgId);

  const sortedServices = useMemo(
    () =>
      [...(services || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [services],
  );

  const firstService = sortedServices[0];

  const currentContact = useMemo<SelectOptionType | null>(() => {
    if (!invoice) {
      return null;
    }

    return {
      value: invoice?.customerId || "",
      label: invoice?.customer?.name || "",
      avatar: getInitialsFromFullName(invoice?.customer?.name || ""),
    };
  }, [invoice]);

  const invoiceCustomers = useMemo<SelectOptionType[]>(
    () =>
      invoice?.invoiceCustomers?.map((customer) => ({
        value: customer?.customerId || "",
        label: customer?.customers?.name || "",
        avatar: getInitialsFromFullName(customer?.customers?.name || ""),
      })) || [],
    [invoice],
  );

  const selectedTaxRate = useMemo(() => {
    const invoiceTaxCodeId = invoice?.invoiceItem?.[0]?.taxCodeId;

    return (
      taxRateData?.find((item) => item.id === invoiceTaxCodeId) ||
      taxRateData?.[0] ||
      null
    );
  }, [invoice?.invoiceItem, taxRateData]);

  const initialItems = useMemo(() => {
    if (!invoice?.invoiceItem?.length) {
      return firstService ? [createDefaultInvoiceItem(firstService)] : [];
    }

    return invoice.invoiceItem
      .slice()
      .sort((a, b) => {
        const firstDate = moment(a?.createdAt).unix();
        const secondDate = moment(b?.createdAt).unix();

        return firstDate - secondDate;
      })
      .map((item) => ({
        clientRowId: item.id || uuidv4(),
        service: item.billingServiceId,
        description: item.description,
        qty: item.quantity,
        rate: item.rate.toString(),
        amount: item.amount,
        id: item.id,
        workTypeId: item.workTypeId,
        assignedUserId: item.assignedUserId,
        invoiceType: item.invoiceType,
        workId: item.workId,
        workName: item.work?.name || undefined,
        duration: item.duration,
        taxCodeId: item?.taxCodeId,
      }));
  }, [firstService, invoice?.invoiceItem]);

  const initialValues = useMemo<InvoiceFormInitialValues>(
    () => ({
      invoiceNo: invoice?.invoiceNo || 0,
      currentContact,
      invoiceCustomers,
      invoiceDate: invoice?.invoiceDate
        ? new Date(invoice.invoiceDate)
        : new Date(),
      dueDate: invoice?.dueDate ? new Date(invoice.dueDate) : new Date(),
      term: invoice?.termId || "",
      selectedUserId: invoice?.assignedToUserId || null,
      isIncludeTax: invoice?.isIncluded || false,
      discount: invoice?.discount?.toString() || "",
      discountType:
        invoice?.discountTypeId === discountTypes.$.toLowerCase() ? "$" : "%",
      notes: invoice?.notes || "",
      taxRateValue: selectedTaxRate,
      items: initialItems,
    }),
    [currentContact, initialItems, invoice, invoiceCustomers, selectedTaxRate],
  );

  const handleSubmit = async (values: InvoiceFormSubmitValues) => {
    if (!invoice) {
      return;
    }

    const existingCustomerIds =
      invoice?.invoiceCustomers?.map((item) => item?.customerId) || [];

    const selectedCustomerIds = values.invoiceCustomers.map(
      (item) => item.value,
    );

    const body: IEditInvoice = {
      replaceInvoiceItems: true,
      isIncluded: values.isIncludeTax,
      id: invoice?.id || "",
      notes: values.notes || "",
      assignedToUserId: values.selectedUserId || null,
      orgId,
      createdByUserId: user.id,
      customerId: values.currentContact?.value || "",
      invoiceNo: values.invoiceNo,
      // Saved tax = sum of the lines' taxes (QA batch 17, was always 0).
      tax: +(values.totalTax || 0).toFixed(2),
      // "" = Custom date (no term); the server's TermId is a Guid or null.
      termId: values.term || null,
      balance: +(values.balanceDue || 0).toFixed(2),
      discount: parseMoney(values.discount),
      paymentRecieved: +(paymentsRecieved || 0).toFixed(2),
      total: +values.total.toFixed(2),
      invoiceDate: moment(values.invoiceDate).startOf("day").toISOString(),
      dueDate: moment(values.dueDate).startOf("day").toISOString(),
      discountTypeId: discountTypes[values.discountType].toLowerCase(),
      invoiceCustomers: findMissingElements(
        selectedCustomerIds,
        existingCustomerIds,
      ).map((item) => ({ customerId: item })),
      invoiceClientsIds: findMissingElements(
        existingCustomerIds,
        selectedCustomerIds,
      ),
      invoiceStatusId: invoice?.invoiceStatusId || null,
      invoiceItem: values.items.map((item) => ({
        id: item.id,
        description: item.description,
        billingServiceId: item.service,
        amount: item.amount,
        rate: parseMoney(item.rate),
        quantity: item.qty,
        invoiceType: item.invoiceType,
        assignedUserId: item.assignedUserId,
        workTypeId: item.workTypeId,
        workId: item.workId,
        duration: item.duration,
        // BILL-039 (QA batch 17): each line keeps its own tax code.
        taxCodeId: item.taxCodeId || values.taxRateValue?.id || "",
      })),
    };

    await updateInvoice(body).unwrap();

    // Tracked time added while editing: mark it billed and keep its "View
    // work" record, like a new invoice does (QA C1, 2026-10-22). The server
    // marks it billed too.
    if (values.billedWorks.length > 0 && invoice?.id) {
      await updateBilledStatus(
        values.billedWorks.map((item) => item.id),
      ).unwrap();
      await addBilledWork(
        billedWorkRecords(values.billedWorks, invoice.id),
      ).unwrap();
    }

    setView("details");
    setSearchParams({});
  };

  if (
    isLoadingTerms ||
    isLoadingServices ||
    isLoadingTaxRate ||
    isLoadingInvoice ||
    isLoadingUsers ||
    isLoadingPaymentsRecieved
  ) {
    return <LottieLoading />;
  }

  if (
    !services ||
    !services.length ||
    !taxRateData ||
    !taxRateData.length ||
    !users ||
    !invoice
  ) {
    return (
      <div className={"text-center text-lg font-semibold"}>
        <div className={"text-center text-gray-800"}>
          Check your services and tax rate in settings
        </div>

        <div
          className={"link-primary cursor-pointer text-center hover:underline"}
          onClick={() => navigate("/settings/billing")}>
          Go to settings
        </div>
      </div>
    );
  }

  return (
    <InvoiceForm
      mode={"edit"}
      formKey={`edit-${invoice.id}-${invoice.invoiceNo}-${
        invoice.invoiceItem?.length || 0
      }`}
      title={"Edit Invoice No."}
      initialValues={initialValues}
      terms={terms || []}
      services={sortedServices}
      taxRateData={taxRateData}
      users={users}
      isSubmitting={isLoadingUpdate}
      paymentsReceived={paymentsRecieved || 0}
      submitLabel={"Save Invoice"}
      existingInvoiceItems={invoice?.invoiceItem}
      onCancel={() => {
        setView("details");
        setSearchParams({});
      }}
      onSubmit={handleSubmit}
      footer={
        <div className={"text-base font-semibold text-gray-800"}>
          {moment(invoice?.createdAt).format("MMM DD, YYYY")} Invoice created by{" "}
          {invoice?.createdByUser &&
            `${invoice?.createdByUser?.userProfile?.firstName} ${invoice?.createdByUser?.userProfile?.lastName}`}
        </div>
      }
    />
  );
};

export default EditInvoice;
